import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Switch,
  Alert,
  LinearProgress,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  CreditCard,
  Train,
  Store as StoreIcon,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import api from "../../services/api";

// 電子マネータイプ定義
const EMONEY_TYPES = {
  transport: {
    label: "交通系IC",
    icon: <Train />,
    types: [
      { id: "suica", name: "Suica" },
      { id: "pasmo", name: "PASMO" },
      { id: "icoca", name: "ICOCA" },
      { id: "toica", name: "TOICA" },
      { id: "manaca", name: "manaca" },
      { id: "kitaca", name: "Kitaca" },
      { id: "sugoca", name: "SUGOCA" },
      { id: "nimoca", name: "nimoca" },
      { id: "hayakaken", name: "はやかけん" },
    ],
  },
  retail: {
    label: "流通系・ポストペイ",
    icon: <StoreIcon />,
    types: [
      { id: "nanaco", name: "nanaco" },
      { id: "waon", name: "WAON" },
      { id: "edy", name: "楽天Edy" },
      { id: "id", name: "iD" },
      { id: "quicpay", name: "QUICPay" },
    ],
  },
};

// 決済代行会社
const PAYMENT_PROVIDERS = [
  { id: "square", name: "Square" },
  { id: "stera", name: "stera" },
  { id: "airpay", name: "Airペイ" },
  { id: "stores", name: "STORES 決済" },
  { id: "coiney", name: "Coiney" },
  { id: "other", name: "その他" },
];

const EmoneySettings = () => {
  const { storeId } = useParams();
  const [emoneySettings, setEmoneySettings] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({
    transport: true,
    retail: true,
  });

  // 初期設定を生成
  const initializeSettings = useCallback(() => {
    const allTypes = [
      ...EMONEY_TYPES.transport.types,
      ...EMONEY_TYPES.retail.types,
    ];
    return allTypes.map((type) => ({
      emoneyType: type.id,
      isEnabled: false,
      paymentProvider: "",
      terminalId: "",
      feeRate: 0,
    }));
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/stores/${storeId}/emoney-settings`);
      if (response.data && response.data.length > 0) {
        // サーバーからの設定をマージ
        const initialSettings = initializeSettings();
        const mergedSettings = initialSettings.map((initial) => {
          const serverSetting = response.data.find(
            (s) => s.emoneyType === initial.emoneyType
          );
          return serverSetting ? { ...initial, ...serverSetting } : initial;
        });
        setEmoneySettings(mergedSettings);
      } else {
        setEmoneySettings(initializeSettings());
      }
    } catch (err) {
      console.error("電子マネー設定の取得に失敗しました:", err);
      if (err.response?.status === 404) {
        setEmoneySettings(initializeSettings());
      } else {
        setError("電子マネー設定の取得に失敗しました");
      }
    } finally {
      setLoading(false);
    }
  }, [storeId, initializeSettings]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleToggle = (emoneyType) => {
    setEmoneySettings((prev) =>
      prev.map((setting) =>
        setting.emoneyType === emoneyType
          ? { ...setting, isEnabled: !setting.isEnabled }
          : setting
      )
    );
  };

  const handleChange = (emoneyType, field, value) => {
    setEmoneySettings((prev) =>
      prev.map((setting) =>
        setting.emoneyType === emoneyType
          ? { ...setting, [field]: value }
          : setting
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      // 有効な設定のみ送信
      const enabledSettings = emoneySettings.filter((s) => s.isEnabled);
      await api.put(`/api/stores/${storeId}/emoney-settings`, {
        settings: enabledSettings,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("電子マネー設定の更新に失敗しました:", err);
      setError(
        err.response?.data?.message || "電子マネー設定の更新に失敗しました"
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const getSettingForType = (typeId) => {
    return emoneySettings.find((s) => s.emoneyType === typeId) || {};
  };

  const enabledCount = emoneySettings.filter((s) => s.isEnabled).length;

  return (
    <Box aria-busy={loading || saving}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Typography variant="h4" gutterBottom>
        電子マネー設定
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        利用する電子マネー決済の種類と決済代行会社を設定します。
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          設定を更新しました
        </Alert>
      )}

      {/* サマリー */}
      <Box sx={{ mb: 3 }}>
        <Chip
          icon={<CreditCard />}
          label={`${enabledCount}種類の電子マネーが有効`}
          color={enabledCount > 0 ? "primary" : "default"}
          variant="outlined"
        />
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* カテゴリごとのカード */}
          {Object.entries(EMONEY_TYPES).map(([category, categoryData]) => (
            <Grid item xs={12} key={category}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={() => toggleCategory(category)}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      {categoryData.icon}
                      <Typography variant="h6">{categoryData.label}</Typography>
                      <Chip
                        size="small"
                        label={`${
                          emoneySettings.filter(
                            (s) =>
                              s.isEnabled &&
                              categoryData.types.some(
                                (t) => t.id === s.emoneyType
                              )
                          ).length
                        }/${categoryData.types.length}`}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <IconButton size="small">
                      {expandedCategories[category] ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )}
                    </IconButton>
                  </Box>

                  <Collapse in={expandedCategories[category]}>
                    <TableContainer component={Paper} sx={{ mt: 2 }} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>電子マネー</TableCell>
                            <TableCell align="center">有効</TableCell>
                            <TableCell>決済代行会社</TableCell>
                            <TableCell>端末ID</TableCell>
                            <TableCell>手数料率(%)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {categoryData.types.map((type) => {
                            const setting = getSettingForType(type.id);
                            return (
                              <TableRow key={type.id}>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="medium">
                                    {type.name}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Switch
                                    checked={setting.isEnabled || false}
                                    onChange={() => handleToggle(type.id)}
                                    disabled={saving}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <FormControl
                                    size="small"
                                    fullWidth
                                    disabled={!setting.isEnabled || saving}
                                  >
                                    <Select
                                      value={setting.paymentProvider || ""}
                                      onChange={(e) =>
                                        handleChange(
                                          type.id,
                                          "paymentProvider",
                                          e.target.value
                                        )
                                      }
                                      displayEmpty
                                    >
                                      <MenuItem value="">
                                        <em>選択</em>
                                      </MenuItem>
                                      {PAYMENT_PROVIDERS.map((provider) => (
                                        <MenuItem
                                          key={provider.id}
                                          value={provider.id}
                                        >
                                          {provider.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    value={setting.terminalId || ""}
                                    onChange={(e) =>
                                      handleChange(
                                        type.id,
                                        "terminalId",
                                        e.target.value
                                      )
                                    }
                                    disabled={!setting.isEnabled || saving}
                                    placeholder="端末ID"
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={setting.feeRate || 0}
                                    onChange={(e) =>
                                      handleChange(
                                        type.id,
                                        "feeRate",
                                        parseFloat(e.target.value) || 0
                                      )
                                    }
                                    disabled={!setting.isEnabled || saving}
                                    inputProps={{
                                      min: 0,
                                      max: 10,
                                      step: 0.01,
                                    }}
                                    sx={{ width: 80 }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {/* 説明 */}
          <Grid item xs={12}>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>電子マネー決済について</strong>
                <br />
                有効にした電子マネーがPOS画面の決済方法として表示されます。
                決済代行会社と端末IDは、実際の決済処理に使用されます。
              </Typography>
            </Alert>
          </Grid>

          {/* 保存ボタン */}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={saving}
            >
              {saving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "設定を保存"
              )}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default EmoneySettings;
